import React from 'react';
import { Subscription } from 'api/rest/myaliro_billing';
import Button from 'views/Dashboards-B2B/components/Button';
import * as S from './CurrentSubscription.styled';

interface CurrentSubscriptionProps {
    subscription: Subscription | null;
    onChangePackage: () => void;
    onCancelSubscription: () => void;
    onGetStarted: () => void;
}

const CurrentSubscription: React.FC<CurrentSubscriptionProps> = ({
    subscription,
    onChangePackage,
    onCancelSubscription,
    onGetStarted,
}) => {
    if (!subscription) {
        return (
            <S.Container>
                <S.SectionHeader>
                    <S.SectionTitle>Current Subscription</S.SectionTitle>
                </S.SectionHeader>
                <S.EmptyState>
                    <S.EmptyStateTitle>No Active Subscription</S.EmptyStateTitle>
                    <S.EmptyStateText>
                        You don't have an active subscription yet. Subscribe to a package to start
                        using MyAliro billing.
                    </S.EmptyStateText>
                    <Button background="purple" onClick={onGetStarted}>
                        Get Started
                    </Button>
                </S.EmptyState>
            </S.Container>
        );
    }

    // Calculate days remaining in trial period (Stripe trial_end is Unix timestamp)
    const getTrialDaysRemaining = (): number | null => {
        if (!subscription.trial_end) return null;
        const trialEnd = new Date(subscription.trial_end * 1000); // Convert Unix timestamp to Date
        const now = new Date();
        const daysRemaining = Math.ceil(
            (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysRemaining > 0 ? daysRemaining : 0;
    };

    const trialDaysRemaining = getTrialDaysRemaining();
    const isInTrial = trialDaysRemaining !== null && trialDaysRemaining > 0;

    // Format next billing date from Stripe current_period_end (Unix timestamp)
    const getNextBillingDateFormatted = (): string => {
        const date = new Date(subscription.current_period_end * 1000);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Format subscription start date from current_period_start
    const getStartDateFormatted = (): string => {
        const date = new Date(subscription.current_period_start * 1000);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Format status for display
    const getStatusBadge = (): string => {
        const statusMap: Record<string, string> = {
            active: 'ACTIVE',
            past_due: 'PAST DUE',
            canceled: 'CANCELED',
            incomplete: 'INCOMPLETE',
            trialing: 'TRIAL',
            unpaid: 'UNPAID',
        };
        return statusMap[subscription.status] || subscription.status.toUpperCase();
    };

    // Get status badge color
    const getStatusColor = (): 'ACTIVE' | 'PENDING_APPROVAL' | 'SUSPENDED' | 'CANCELLED' => {
        if (subscription.status === 'active' || subscription.status === 'trialing') return 'ACTIVE';
        if (subscription.status === 'past_due' || subscription.status === 'unpaid')
            return 'SUSPENDED';
        if (subscription.status === 'canceled') return 'CANCELLED';
        return 'PENDING_APPROVAL';
    };

    return (
        <S.Container>
            <S.SectionHeader>
                <S.SectionTitle>Current Subscription</S.SectionTitle>
                <S.StatusBadge status={getStatusColor()}>{getStatusBadge()}</S.StatusBadge>
            </S.SectionHeader>

            {(subscription.status === 'incomplete' || subscription.status === 'past_due') && (
                <S.InfoMessage type="warning">
                    Your subscription payment is incomplete. Please update your payment method in
                    the billing portal.
                </S.InfoMessage>
            )}

            {subscription.status === 'unpaid' && (
                <S.InfoMessage type="warning">
                    Your subscription has been suspended due to payment issues. Please update your
                    payment method.
                </S.InfoMessage>
            )}

            {subscription.cancel_at_period_end && (
                <S.InfoMessage type="info">
                    Your subscription will be cancelled on {getNextBillingDateFormatted()}.
                </S.InfoMessage>
            )}

            {subscription.status === 'canceled' && subscription.canceled_at && (
                <S.InfoMessage type="info">
                    Your subscription was cancelled on{' '}
                    {new Date(subscription.canceled_at * 1000).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                    .
                </S.InfoMessage>
            )}

            {isInTrial && subscription.trial_end && (
                <S.InfoMessage type="success">
                    <S.FreePeriodBadge>
                        {trialDaysRemaining} days remaining in trial
                    </S.FreePeriodBadge>{' '}
                    Your trial ends on{' '}
                    {new Date(subscription.trial_end * 1000).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                    . Billing will begin after this date.
                </S.InfoMessage>
            )}

            <S.SubscriptionGrid>
                <S.InfoCard>
                    <S.InfoLabel>Amount</S.InfoLabel>
                    <S.InfoValue>
                        {isInTrial ? (
                            <span style={{ color: '#10b981', fontWeight: 700 }}>FREE TRIAL</span>
                        ) : (
                            <span>
                                ${subscription.amount.toFixed(2)}{' '}
                                {subscription.currency.toUpperCase()}/{subscription.billing_period}
                            </span>
                        )}
                    </S.InfoValue>
                </S.InfoCard>

                <S.InfoCard>
                    <S.InfoLabel>Billing Period</S.InfoLabel>
                    <S.InfoValue>
                        <S.BillingPeriodBadge>
                            {subscription.billing_period.toUpperCase()}
                        </S.BillingPeriodBadge>
                    </S.InfoValue>
                </S.InfoCard>

                <S.InfoCard>
                    <S.InfoLabel>Next Billing Date</S.InfoLabel>
                    <S.InfoValue style={{ fontSize: '14px', fontWeight: 500 }}>
                        {getNextBillingDateFormatted()}
                    </S.InfoValue>
                </S.InfoCard>

                <S.InfoCard>
                    <S.InfoLabel>Current Period Start</S.InfoLabel>
                    <S.InfoValue style={{ fontSize: '14px', fontWeight: 500 }}>
                        {getStartDateFormatted()}
                    </S.InfoValue>
                </S.InfoCard>

                {subscription.payment_method?.card && (
                    <S.InfoCard>
                        <S.InfoLabel>Payment Method</S.InfoLabel>
                        <S.InfoValue style={{ fontSize: '14px', fontWeight: 500 }}>
                            {subscription.payment_method.card.brand.toUpperCase()} ••••{' '}
                            {subscription.payment_method.card.last4}
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                Expires {subscription.payment_method.card.exp_month}/
                                {subscription.payment_method.card.exp_year}
                            </div>
                        </S.InfoValue>
                    </S.InfoCard>
                )}
            </S.SubscriptionGrid>

            {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                <S.ButtonGroup>
                    <Button background="purple" onClick={onChangePackage}>
                        Change Package
                    </Button>
                    <Button
                        background="transparent"
                        border="1px solid #d1d5db"
                        color="#374151"
                        onClick={onCancelSubscription}
                    >
                        Cancel Subscription
                    </Button>
                </S.ButtonGroup>
            )}
        </S.Container>
    );
};

export default CurrentSubscription;
